import { Language } from '../types/menu';

interface QualityCheck {
  score: number;
  issues: string[];
  suggestions: string[];
}

interface TranslationRules {
  minLength: number;
  maxLength: number;
  forbiddenWords: string[];
  requiredPatterns: RegExp[];
  specialCharacters: RegExp;
}

const languageRules: { [key: string]: TranslationRules } = {
  fr: {
    minLength: 3,
    maxLength: 100,
    forbiddenWords: ['le', 'la', 'les', 'de', 'du', 'des'],
    requiredPatterns: [/^[A-Z]/], // Must start with capital letter
    specialCharacters: /[éèêëàâäôöûüçîï]/
  },
  ar: {
    minLength: 2,
    maxLength: 100,
    forbiddenWords: ['ال', 'من', 'في', 'على'],
    requiredPatterns: [/[\u0600-\u06FF]/], // Must contain Arabic characters
    specialCharacters: /[\u0600-\u06FF]/
  },
  en: {
    minLength: 3,
    maxLength: 100,
    forbiddenWords: ['the', 'a', 'an'],
    requiredPatterns: [/^[A-Z]/], // Must start with capital letter
    specialCharacters: /[A-Za-z]/
  }
};

export const checkTranslationQuality = (
  text: string,
  language: Language,
  context: 'name' | 'description' = 'name'
): QualityCheck => {
  const rules = languageRules[language];
  if (!rules) {
    return {
      score: 0,
      issues: [`No quality rules configured for language: ${language}`],
      suggestions: []
    };
  }
  let score = 100;
  const issues: string[] = [];
  const suggestions: string[] = [];

  // Check length
  if (text.length < rules.minLength) {
    issues.push(`Text is too short (minimum ${rules.minLength} characters)`);
    score -= 20;
  }

  if (text.length > rules.maxLength) {
    issues.push(`Text is too long (maximum ${rules.maxLength} characters)`);
    score -= 20;
  }

  // Check for forbidden words
  const words = text.toLowerCase().split(/\s+/);
  const foundForbiddenWords = rules.forbiddenWords.filter(word => 
    words.includes(word.toLowerCase())
  );
  
  if (foundForbiddenWords.length > 0) {
    issues.push(`Contains unnecessary articles: ${foundForbiddenWords.join(', ')}`);
    suggestions.push('Remove unnecessary articles');
    score -= 10 * foundForbiddenWords.length;
  }

  // Check required patterns
  rules.requiredPatterns.forEach((pattern, index) => {
    if (!pattern.test(text)) {
      issues.push(`Text doesn't match required pattern #${index + 1}`);
      score -= 15;
    }
  });

  // Check for special characters
  if (!rules.specialCharacters.test(text)) {
    issues.push(`Text doesn't contain required characters for ${language}`);
    score -= 25;
  }

  // Context-specific checks
  if (context === 'name') {
    if (text.length > 30) {
      issues.push('Name is too long for display');
      score -= 15;
    }
  } else if (context === 'description') {
    if (text.length < 10) {
      issues.push('Description is too short');
      suggestions.push('Add more details to the description');
      score -= 15;
    }
  }

  return {
    score: Math.max(0, score),
    issues,
    suggestions
  };
}; 